/**
 * MCP grants — the AI assistants authorized to act on this network.
 *
 * Unlike `useSessions`, there *is* an administrative variant. A browser session is one
 * person's own credential; a grant is a service principal that acts on the
 * organization's network, so "what agents can reach us" is a question the organization
 * gets to ask. The server still filters the administrative list per row, against the
 * context each grant is pinned to.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '../config';

const backendApi = config.backendApi;

export interface McpGrant {
  /** The service principal's id — what a revoke takes. */
  id: string;
  /** The name chosen at consent, e.g. "Claude — network assistant". */
  name: string;
  /** A grant is pinned to exactly one context; the agent can see nothing outside it. */
  contextId: string;
  contextName: string | null;
  /** `read_plan` or `read_plan_execute`. */
  preset: string;
  /** The OAuth client holding it. Absent until the client has exchanged a token. */
  clientId: string | null;
  createdAt: string;
  createdByUserId: string;
  createdByName: string | null;
  /** Null means authorized but never used — the easy ones to take back. */
  lastUsedAt: string | null;
  /** Whether the caller approved it. The admin list mixes its own rows with others'. */
  isOwn: boolean;
}

interface McpGrantListResponse {
  grants: McpGrant[];
}

export const MCP_GRANTS_QUERY_KEY = ['mcp-grants'] as const;
export const MCP_GRANTS_ALL_QUERY_KEY = ['mcp-grants', 'all'] as const;

async function fetchGrants(path: string): Promise<McpGrant[]> {
  const response = await axios.get<McpGrantListResponse>(`${backendApi}/api/mcp/grants${path}`, {
    withCredentials: true,
  });
  // Defensive: hand callers an array rather than letting an unexpected shape reach a `.map`.
  return Array.isArray(response.data.grants) ? response.data.grants : [];
}

/** The agents you authorized. */
export function useMcpGrants() {
  return useQuery<McpGrant[]>({
    queryKey: MCP_GRANTS_QUERY_KEY,
    queryFn: () => fetchGrants('/'),
  });
}

/**
 * Every agent in the organization you may administer, yours included.
 *
 * `enabled` rather than a permission check in the component: the server already
 * decides row by row, and a caller with no administrative reach simply sees their own.
 */
export function useAllMcpGrants(enabled = true) {
  return useQuery<McpGrant[]>({
    queryKey: MCP_GRANTS_ALL_QUERY_KEY,
    queryFn: () => fetchGrants('/all'),
    enabled,
  });
}

export function useRevokeMcpGrant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${backendApi}/api/mcp/grants/${id}`, { withCredentials: true });
    },
    onSuccess: () => {
      // Both lists can hold the same row, so both go stale together.
      void queryClient.invalidateQueries({ queryKey: MCP_GRANTS_QUERY_KEY });
    },
  });
}
